import { useAuth } from './auth';
import i18n from './i18n';
import type { TradeKey } from './schemas';

/* ---------------------------------------------------------------------------
   המונחים והתכונות לפי התחום של העסק — עותק של src/common/trades.ts בשרת
   (בלי סוגי העבודות, שנוצרים בשרת בהרשמה). מה שמשתנה בממשק: איך קוראים
   לעובד, והאם יש ציוד אצל הלקוח עם טיפול תקופתי.
   --------------------------------------------------------------------------- */

export interface TradeUi {
  label: string;
  /** אייקון מ-lucide, לבחירת התחום בהרשמה. */
  icon: string;
  hint: string;
  worker: { one: string; many: string };
  equipment: { label: string; kind: string; intervalMonths: number } | null;
}

/** אייקון ומחזור הטיפול לכל תחום. הטקסטים — common:trades.<KEY>.* (מתורגמים). */
const TRADE_META: Record<TradeKey, { icon: string; intervalMonths: number | null }> = {
  HVAC: { icon: 'snowflake', intervalMonths: 12 },
  ELECTRICAL: { icon: 'zap', intervalMonths: 60 },
  PLUMBING: { icon: 'droplets', intervalMonths: 12 },
  CARPENTRY: { icon: 'hammer', intervalMonths: null },
  RENOVATION: { icon: 'hard-hat', intervalMonths: null },
  BUILDING_MAINTENANCE: { icon: 'building-2', intervalMonths: 6 },
  CLEANING: { icon: 'sparkles', intervalMonths: null },
  GARDENING: { icon: 'trees', intervalMonths: 1 },
  RETAIL: { icon: 'store', intervalMonths: null },
  OTHER: { icon: 'briefcase', intervalMonths: null },
  OFFICE: { icon: 'building', intervalMonths: null },
};

// getters: הטקסט נקרא בשפה הנוכחית בכל גישה, והצורה (TRADES[k].worker.one) לא משתנה.
function tradeUi(key: TradeKey): TradeUi {
  const { icon, intervalMonths } = TRADE_META[key];
  const worker = {
    get one() {
      return i18n.t(`common:trades.${key}.worker.one`);
    },
    get many() {
      return i18n.t(`common:trades.${key}.worker.many`);
    },
  };
  const equipment =
    intervalMonths === null
      ? null
      : {
          intervalMonths,
          get label() {
            return i18n.t(`common:equipment.${key as EquipmentTrade}.label`);
          },
          get kind() {
            return i18n.t(`common:equipment.${key as EquipmentTrade}.kind`);
          },
        };
  return {
    icon,
    worker,
    equipment,
    get label() {
      return i18n.t(`common:trades.${key}.label`);
    },
    get hint() {
      return i18n.t(`common:trades.${key}.hint`);
    },
  };
}

type EquipmentTrade = 'HVAC' | 'ELECTRICAL' | 'PLUMBING' | 'BUILDING_MAINTENANCE' | 'GARDENING';

export const TRADES = Object.fromEntries(
  (Object.keys(TRADE_META) as TradeKey[]).map((k) => [k, tradeUi(k)]),
) as Record<TradeKey, TradeUi>;

/** הפרופיל של העסק המחובר. בלי סשן (דפים ציבוריים) — ניטרלי. */
export function useTrade(): TradeUi & { key: TradeKey } {
  const { session } = useAuth();
  const key = session?.tenant?.trade ?? 'OTHER';
  return { key, ...TRADES[key] };
}
